import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Plus, RefreshCw, Search } from 'lucide-react'
import { fetchMachines, fetchTablets } from '@/services/api'
import type { Machine } from '@/types/machine'
import type { Tablet } from '@/types/tablet'
import TabletDialog from '@/components/TabletDialog'

const headerClass =
  'px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap'
const cellClass = 'px-4 py-2 text-sm text-gray-900 whitespace-nowrap'

export default function TabletsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [searchText, setSearchText] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editTablet, setEditTablet] = useState<Tablet | null>(null)

  const tabletsQuery = useQuery({
    queryKey: ['tablets'],
    queryFn: fetchTablets,
  })
  const machinesQuery = useQuery({
    queryKey: ['machines'],
    queryFn: fetchMachines,
  })

  const machineById = useMemo(() => {
    const map = new Map<string, Machine>()
    for (const machine of machinesQuery.data ?? []) {
      map.set(machine.machineId, machine)
    }
    return map
  }, [machinesQuery.data])

  const filteredTablets = useMemo(() => {
    const tablets = tabletsQuery.data ?? []
    const query = searchText.trim().toLowerCase()
    if (!query) return tablets
    return tablets.filter((tablet) => {
      const machine = tablet.linkedMachineId
        ? machineById.get(tablet.linkedMachineId)
        : null
      const haystack = [
        tablet.name,
        tablet.model,
        tablet.edition,
        tablet.serialNumber,
        tablet.imei,
        tablet.productNumber,
        tablet.mfgDate,
        tablet.note,
        machine?.serialNumber,
        machine?.customer,
      ]
        .filter(Boolean)
        .join('\n')
        .toLowerCase()
      return haystack.includes(query)
    })
  }, [tabletsQuery.data, machineById, searchText])

  const handleSaved = () => {
    setDialogOpen(false)
    queryClient.invalidateQueries({ queryKey: ['tablets'] })
  }

  if (tabletsQuery.isLoading || machinesQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <span className="ml-3 text-gray-500">{t('common.loading')}</span>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="relative flex-1 min-w-[12rem] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('tablets.searchPlaceholder')}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => tabletsQuery.refetch()}
            disabled={tabletsQuery.isFetching}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            title={t('common.refresh')}
          >
            <RefreshCw
              className={`h-4 w-4 ${tabletsQuery.isFetching ? 'animate-spin' : ''}`}
            />
          </button>
          <button
            onClick={() => {
              setEditTablet(null)
              setDialogOpen(true)
            }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors whitespace-nowrap"
          >
            <Plus className="h-4 w-4" />
            {t('tablets.register')}
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-500 mb-4">{t('tablets.subtitle')}</p>

      {/* Error */}
      {tabletsQuery.isError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {t('tablets.fetchError')}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={headerClass}>{t('tablets.name')}</th>
              <th className={headerClass}>{t('tablets.model')}</th>
              <th className={headerClass}>{t('machines.edition')}</th>
              <th className={headerClass}>{t('tablets.serialNumber')}</th>
              <th className={headerClass}>{t('tablets.imei')}</th>
              <th className={headerClass}>{t('tablets.productNumber')}</th>
              <th className={headerClass}>{t('tablets.mfgDate')}</th>
              <th className={headerClass}>{t('tablets.linkedMachine')}</th>
              <th className={headerClass}>{t('tablets.note')}</th>
              <th className={headerClass}>{t('machines.updatedAt')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredTablets.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="px-4 py-10 text-center text-sm text-gray-400"
                >
                  {t('tablets.empty')}
                </td>
              </tr>
            ) : (
              filteredTablets.map((tablet) => {
                const machine = tablet.linkedMachineId
                  ? machineById.get(tablet.linkedMachineId)
                  : null
                return (
                  <tr
                    key={tablet.tabletId}
                    onClick={() => {
                      setEditTablet(tablet)
                      setDialogOpen(true)
                    }}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className={cellClass}>{tablet.name || '-'}</td>
                    <td className={cellClass}>{tablet.model || '-'}</td>
                    <td className={cellClass}>{tablet.edition || '-'}</td>
                    <td className={`${cellClass} font-mono`}>
                      {tablet.serialNumber || '-'}
                    </td>
                    <td className={`${cellClass} font-mono`}>
                      {tablet.imei || '-'}
                    </td>
                    <td className={`${cellClass} font-mono`}>
                      {tablet.productNumber || '-'}
                    </td>
                    <td className={cellClass}>{tablet.mfgDate || '-'}</td>
                    <td className={cellClass}>
                      {machine ? (
                        <Link
                          to={`/machines/${machine.machineId}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-600 hover:text-primary-700 hover:underline"
                        >
                          {machine.serialNumber ?? t('detail.noSerial')}
                          {machine.customer ? ` (${machine.customer})` : ''}
                        </Link>
                      ) : (
                        <span className="text-gray-400">
                          {t('tablets.unlinked')}
                        </span>
                      )}
                    </td>
                    <td
                      className={`${cellClass} max-w-[16rem] truncate`}
                      title={tablet.note}
                    >
                      {tablet.note || '-'}
                    </td>
                    <td className={`${cellClass} text-gray-500`}>
                      {tablet.updatedAt
                        ? new Date(tablet.updatedAt).toLocaleDateString()
                        : '-'}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <TabletDialog
        open={dialogOpen}
        tablet={editTablet}
        machines={machinesQuery.data ?? []}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  )
}
